import { useEffect, useState } from 'react';

import { VStack, useToast, useDisclosure, Flex, Text, Button } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import PertaminaLogo from '@/assets/PertaminaLogo';
import { RootState } from '@/redux/store';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import GenericMenus from '../Menus/GenericMenus';
import { ACTIVE_MENU, DEFAULT_COMPANY_NAME, DURATION_TOAST, ROLES } from '@/common/constants';
import { setAppId, setCompanyName } from '@/redux/slices/accountSlice';
import { extractCurrentPath } from '@/utils/letterPath';
import ModalLogOut from '../Modals/ModalLogOut';
import styles from './styles/Header.module.css';
import LogOutIcon from '@/assets/LogOutIcon';
import CloseIcon from '@/assets/CloseIcon';
import MenuIcon from '@/assets/MenuIcon';
import { HeaderProps } from './types';

const invoiceCreationPaths = [
    'Manage Invoices Inspector Add',
    'Manage Invoices Ship Owner Add'
];

const isInvoiceCreationPath = (path: string) => {
    return invoiceCreationPaths.includes(path.trim());
};

const HeaderSidebarMobile = ({ handleSetIsAuthenticated, isShowMenu = true }: HeaderProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const toast = useToast();
    const role = useAppSelector((state: RootState) => state.account.roleName);
    const companyName = useAppSelector((state: RootState) => state.account.companyName) || DEFAULT_COMPANY_NAME;
    const isReviewer = useAppSelector((state: RootState) => state.account.isReviewer);
    const totalNew = useAppSelector((state: RootState) => state.notification.totalNew);
    const menus = useAppSelector((state: RootState) => state.menu.menus);
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [menuStatesGeneric, setMenuStatesGeneric] = useState<Record<string, boolean>>({});
    const [activeMenu, setActiveMenu] = useState('');
    const [username, setUsername] = useState<string>('');
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

    
    const handleToProfile = () => {
        if (!isShowMenu) {
            return;
        }

        if (role === 'super admin' || role === 'inspecting company') {
            navigate('/settings/profile');
        } else {
            navigate('/profile');
        }
    };

    const handleConfirmLogOut = async () => {
        try {
            clearState();
            navigate('/logout');
        } catch (error: unknown) {
            const errorMessage = (error instanceof Error) ? error.message : 'An error occurred while logging you out. Please try again.';

            toast({
                title: 'Logout Failed',
                description: errorMessage,
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    };

    const clearState = () => {
        handleSetIsAuthenticated(false);
        dispatch(setAppId(''));
        dispatch(setCompanyName(''));
        Cookies.remove('access_token');
        Cookies.remove('refresh_token');
        Cookies.remove('profile');
        Cookies.remove('expired_time');
    };

    useEffect(() => {
        const profile = Cookies.get('profile');
        if (profile) {
            const raw = JSON.parse(profile);
            setUsername(raw?.name || '-');
        }
    }, []);

    useEffect(() => {
        const currentActiveMenu = extractCurrentPath(location);
        const isCreatingInvoice = isInvoiceCreationPath(currentActiveMenu);
        setActiveMenu(isCreatingInvoice ? ACTIVE_MENU.CREATE_INVOICE : currentActiveMenu);
        setIsSidebarOpen(false);
    }, [location]);

    const handleNav = (item: string) => {
        navigate(item);
    };

    const handleOpenSubMenuGeneric = (menu: string) => {
        setMenuStatesGeneric((prevState) => ({
            ...prevState,
            [menu]: !prevState[menu as keyof typeof prevState],
        }));
    };

    const handleNavToFirstMenu = () => {
        const firstRoute = menus[0]?.isParent ? menus[0]?.children[0]?.url : menus[0]?.url;
        navigate(firstRoute || '/');
    };

    useEffect(() => {
        const { pathname } = location;

        if (pathname.includes('manage-invoices')) {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                manageinvoices: true
            }));
        } else {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                manageinvoices: false
            }));
        }

        if (pathname.includes('user-management')) {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                usermanagement: true
            }));
        } else {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                usermanagement: false
            }));
        }

        if (pathname.includes('settings')) {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                settings: true
            }));
        } else {
            setMenuStatesGeneric((prevState) => ({
                ...prevState,
                settings: false
            }));
        }
    }, [location]);

    const renderSidebar = () => (
        <div style={{
            height: '100vh',
            width: '100vw',
            zIndex: 1,
            backgroundColor: '#00000080',
            position: 'absolute',
            top: 0,
            left: 0,
        }}>
            <VStack
                as="nav"
                backgroundColor="white"
                p="5"
                pl={0}
                spacing="5"
                align="stretch"
                className='sidebar'
                width='360px'
                overflowY="auto"
                position="absolute"
                right={0}
                zIndex={2}
                paddingLeft={5}
            >
                <Button
                    onClick={() => setIsSidebarOpen(false)}
                    style={{
                        backgroundColor: 'transparent',
                        fontSize: 16,
                        paddingTop: 10,
                        paddingBottom: 10,
                        justifyContent: 'flex-start',
                    }}
                >
                    <CloseIcon />
                    <span style={{ padding: 3 }}>CLOSE</span>
                </Button>
                <VStack align="stretch">
                    <GenericMenus
                        opened={menuStatesGeneric}
                        location={location}
                        handleNav={handleNav}
                        handleOpenSubMenuGeneric={handleOpenSubMenuGeneric}
                        totalNew={totalNew}
                        menus={menus}
                        isReviewer={isReviewer}
                        role={role}
                    />
                </VStack>
                <div>
                    <div className={styles.avatarContainer}>
                        <div className={styles.usernameContainer} onClick={handleToProfile}>
                            <p className={styles.username}>{username || '-'}</p>
                            {isShowMenu && <p className={styles.companyName}>{role === ROLES.SUPER_ADMIN ? DEFAULT_COMPANY_NAME : companyName}</p>}
                            {!isShowMenu && <p className={styles.companyName}>{role === 'inspecting company' ? 'Inspecting Company' : 'Ship Operator'}</p>}
                        </div>
                    </div>
                    <Button
                        onClick={onOpen}
                        style={{
                            backgroundColor: 'transparent',
                            justifyContent: 'flex-start',
                            marginTop: 10,
                            width: '100%',
                        }}
                    >
                        <LogOutIcon />
                        <span style={{ padding: 5 }}>Log Out</span>
                    </Button>
                </div>
                
            </VStack>
        </div>
    );

    return (
        <>
            <ModalLogOut title="Log Out" isOpen={isOpen} onClose={onClose} handleConfirmLogOut={handleConfirmLogOut} />
            <Flex as="header" className={styles.header} justifyContent="space-between" alignItems="center">
                <div style={{ display: 'flex', flexDirection: 'row', paddingLeft: 10 }}>
                    <PertaminaLogo style={{ cursor: 'pointer' }} onClick={handleNavToFirstMenu} />
                    {/*<Heading size="md" color='#000' style={{ marginTop: 6, marginLeft: 8, cursor: 'pointer' }} onClick={handleNavToFirstMenu}>INSPEKTA</Heading>*/}
                </div>
                <Button
                    onClick={() => setIsSidebarOpen(true)}
                    style={{ backgroundColor: 'transparent', marginRight: 10 }}
                ><MenuIcon /></Button>
            </Flex>
            <div style={{
                paddingTop: 10,
                paddingLeft: 25,
            }}>
                <Text fontSize={24} fontWeight="bold">{isShowMenu ? activeMenu : ''}</Text>
            </div>
            {isSidebarOpen && renderSidebar()}
        </>
    );
};

export default HeaderSidebarMobile;
